import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const inputCls = "mt-1.5 w-full bg-transparent border border-white/15 px-3 py-3 text-sm focus:outline-none focus:border-[#1FD1FF]";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { t } = useT();
  const { openAuth } = useAuth();
  const nav = useNavigate();
  const [v, setV] = useState({ next: "", repeat: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (v.next.length < 8) return setError(t("reset.short"));
    if (v.next !== v.repeat) return setError(t("reset.mismatch"));
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: v.next });
      setDone(true);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const toLogin = () => { nav("/"); openAuth("login"); };

  return (
    <main data-testid="reset-password-page" className="max-w-md mx-auto px-4 pt-36 pb-24">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#1FD1FF] mb-3">DEEJAY T-REX</p>
      <h1 className="font-display text-6xl leading-none mb-8">{t("reset.title")}</h1>
      {!token ? (
        <p data-testid="reset-no-token" className="text-[#A8B3C4]">{t("reset.noToken")}</p>
      ) : done ? (
        <div data-testid="reset-done" className="space-y-6">
          <p className="flex gap-3 text-white border border-[#1FD1FF]/40 bg-[#1FD1FF]/10 p-4"><CheckCircle2 className="w-5 h-5 text-[#1FD1FF] shrink-0" />{t("reset.done")}</p>
          <button data-testid="reset-to-login" onClick={toLogin} className="w-full py-3.5 bg-[#1FD1FF] text-black font-mono text-sm uppercase tracking-widest">{t("reset.toLogin")}</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <label className="block"><span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A8B3C4]">{t("reset.new")}</span>
            <input type="password" required autoComplete="new-password" value={v.next} onChange={(e) => setV({ ...v, next: e.target.value })} className={inputCls} data-testid="reset-new-password" /></label>
          <label className="block"><span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A8B3C4]">{t("reset.repeat")}</span>
            <input type="password" required autoComplete="new-password" value={v.repeat} onChange={(e) => setV({ ...v, repeat: e.target.value })} className={inputCls} data-testid="reset-repeat-password" /></label>
          {error && <p data-testid="reset-error" className="text-sm text-[#FCA5A5]">{error}</p>}
          <button data-testid="reset-submit" disabled={busy} className="w-full py-3.5 bg-[#1FD1FF] text-black font-mono text-sm uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-60">
            {busy ? t("auth.wait") : t("reset.save")}
          </button>
        </form>
      )}
    </main>
  );
}
